/**
 * Badge de compteur Wishlist (Growave) sur l'icône cœur du header.
 *
 * Fonctionnement :
 * - Growave stocke son jeton de session (invité ou client) dans
 *   localStorage["GW_TOKEN"].
 * - On appelle l'endpoint Growave `getItemsTotalCount` avec ce jeton en
 *   `Authorization: Bearer` → renvoie { totalCount: n }.
 * - Le résultat est mis en cache (TTL court) pour éviter un appel à chaque page.
 * - Rafraîchissement temps réel :
 *     1. clic sur un bouton Growave — y compris dans un shadow DOM (le clic est
 *        retargeté au host, on utilise composedPath() pour le détecter) ;
 *     2. MutationObserver sur les shadow roots Growave : l'attribut
 *        data-gw-wishlist-items-count du bouton change à chaque ajout/retrait ;
 *     3. interception passive des réponses fetch/XHR de Growave ;
 *     4. filet de sécurité : léger polling tant qu'un changement est suspecté.
 */
(function () {
  'use strict';

  var ENDPOINT =
    '/apps/ssw/storefront-api/wishlist-storefront/v2/getItemsTotalCount?x-gw-current-app=default&designMode=false';
  var CACHE_KEY = 'adiba:wishlist-count';
  var CACHE_TTL = 2 * 60 * 1000; // 2 minutes
  var BUBBLE_ID = 'wishlist-bubble';
  var COUNT_ID = 'wishlist-bubble-count';
  // Seules les écritures Growave déclenchent un rafraîchissement. Ne PAS inclure
  // getItemsTotalCount : fetchCount() passe par le fetch patché, ce qui relançait
  // une rafale à chaque réponse → boucle de requêtes infinie (main thread saturé, INP).
  var GW_WRITE_RE = /wishlist-storefront\/v\d+\/(addItem|deleteItem)/i;
  var WATCH_DELAYS = [0, 1500, 4000, 8000]; // recherches des widgets Growave, puis arrêt
  var inflight = null;
  var refreshTimer = null;
  var pollTimer = null;
  var pollCount = 0;

  function readCache() {
    try {
      var raw = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (raw && Date.now() - raw.t < CACHE_TTL && typeof raw.c === 'number') {
        return raw.c;
      }
    } catch (e) {
      /* cache illisible : on ignore */
    }
    return null;
  }

  function writeCache(count) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ c: count, t: Date.now() }));
    } catch (e) {
      /* localStorage indisponible : on ignore */
    }
  }

  function updateBubble(count) {
    var bubble = document.getElementById(BUBBLE_ID);
    if (!bubble) return;

    var limit = parseInt(bubble.dataset.wishlistBubbleLimit || '100', 10) || 100;
    var textEl = document.getElementById(COUNT_ID);

    if (typeof count !== 'number' || isNaN(count) || count <= 0) {
      bubble.classList.add('visually-hidden');
      if (textEl) {
        textEl.textContent = '';
        textEl.classList.add('hidden');
      }
      return;
    }

    if (textEl) {
      textEl.textContent = count >= limit ? '99+' : String(count);
      textEl.classList.remove('hidden');
    }
    bubble.classList.remove('visually-hidden');
  }

  function fetchCount() {
    var token = null;
    try {
      token = localStorage.getItem('GW_TOKEN');
    } catch (e) {
      token = null;
    }
    if (!token || inflight) return inflight || Promise.resolve();

    inflight = fetch(ENDPOINT, {
      credentials: 'same-origin',
      headers: {
        authorization: 'Bearer ' + token,
        'x-gw-token-strategy': 'growave',
      },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var count = typeof data.totalCount === 'number' ? data.totalCount : null;
        if (count !== null) writeCache(count);
        updateBubble(count);
      })
      .catch(function (err) {
        // En cas d'échec (token expiré, réseau...), on garde la dernière valeur en cache
        var cached = readCache();
        if (cached !== null) updateBubble(cached);
        if (window.console) console.warn('[wishlist-bubble] récupération du compteur échouée :', err);
      })
      .finally(function () {
        inflight = null;
      });
    return inflight;
  }

  /* Rafraîchissement rapproché pendant ~15s après une interaction suspecte */
  function scheduleBurst() {
    clearTimeout(refreshTimer);
    clearInterval(pollTimer);
    pollCount = 0;
    refreshTimer = setTimeout(fetchCount, 800);
    pollTimer = setInterval(function () {
      pollCount += 1;
      fetchCount();
      if (pollCount >= 3) clearInterval(pollTimer);
    }, 2500);
  }

  /** Vrai si l'élément appartient à un widget Growave (tag gw-* ou classe gw-*). */
  function isGrowaveNode(node) {
    if (!(node instanceof Element)) return false;
    if (node.localName.indexOf('gw-') === 0) return true;
    var cls = typeof node.className === 'string' ? node.className : '';
    return cls.indexOf('gw-') !== -1;
  }

  /* 1. Clic sur un widget Growave — y compris dans un shadow DOM (clic retargeté).
     Les autres clics (menu, panier, slideshow…) sont ignorés. */
  function onClicked(event) {
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.some(isGrowaveNode)) scheduleBurst();
  }

  /* 2. MutationObserver sur les shadow roots Growave :
     l'attribut data-gw-wishlist-items-count du bouton change à chaque
     ajout/suppression, même sans clic (widgets réinjectés, etc.). */
  function watchShadowHosts() {
    document.querySelectorAll('.gw-wl-add-to-wishlist-placeholder, [class*="gw-"]').forEach(function (host) {
      if (!host.shadowRoot || host.__wlBubbleObserved) return;
      host.__wlBubbleObserved = true;
      // Attributs seulement : childList se déclenchait à chaque rendu Growave
      // (slider d'avis, etc.) et relançait des rafales de requêtes.
      new MutationObserver(scheduleBurst).observe(host.shadowRoot, {
        subtree: true,
        attributes: true,
        attributeFilter: ['data-gw-wishlist-items-count', 'data-in-wishlist'],
      });
    });
  }

  /* 3. Interception passive des requêtes Growave (fetch + XHR) */
  function installFetchInterceptor() {
    if (typeof window.fetch !== 'function' || window.__wlBubbleFetchPatched) return;
    window.__wlBubbleFetchPatched = true;
    var originalFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url ? args[0].url : '';
      var p = originalFetch.apply(this, args);
      if (GW_WRITE_RE.test(url)) {
        p.then(function () {
          scheduleBurst();
        }).catch(function () {});
      }
      return p;
    };
  }

  function installXhrInterceptor() {
    if (window.__wlBubbleXhrPatched) return;
    window.__wlBubbleXhrPatched = true;
    var originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (typeof url === 'string' && GW_WRITE_RE.test(url)) {
        this.addEventListener('load', scheduleBurst);
      }
      return originalOpen.apply(this, arguments);
    };
  }

  function init() {
    // Affichage immédiat depuis le cache (évite le "flash" sans badge)
    var cached = readCache();
    if (cached !== null) updateBubble(cached);

    installFetchInterceptor();
    installXhrInterceptor();
    fetchCount();

    // Les widgets Growave s'injectent après le chargement : quelques passages en
    // temps libre, puis arrêt (un scan [class*="gw-"] de tout le DOM toutes les 3 s
    // créait des tâches longues qui retardaient les interactions).
    WATCH_DELAYS.forEach(function (delay) {
      setTimeout(function () {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(watchShadowHosts, { timeout: 2000 });
        } else {
          watchShadowHosts();
        }
      }, delay);
    });

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) fetchCount();
    });
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) fetchCount(); // retour via bfcache
    });
    document.addEventListener('click', onClicked, true);

    // Abonnement défensif aux événements du SDK Growave si disponible
    try {
      var emitter = window.gw && window.gw.sdk && window.gw.sdk._emitter;
      if (emitter && typeof emitter.addEventListener === 'function') {
        ['wishlist_updated', 'wishlist:updated', 'gw:wishlist-updated'].forEach(function (name) {
          emitter.addEventListener(name, fetchCount);
        });
      }
    } catch (e) {
      /* SDK absent : les autres déclencheurs suffisent */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
